import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import AuthScreen from './components/AuthScreen';
import MissionPlayer from './components/MissionPlayer';
import TeacherDashboard from './components/TeacherDashboard';
import VocabularyHub from './components/VocabularyHub';
import VocabularyDictionary from './components/VocabularyDictionary';
import FAQSection from './components/FAQSection';
import StudentMessages from './components/StudentMessages';
import StudentTutorChat from './components/StudentTutorChat';
import StudentProgressHistory from './components/StudentProgressHistory';
import WorksheetRepository from './components/WorksheetRepository';
import GrammarHub from './components/GrammarHub';
import UnitVocabularyHub from './components/UnitVocabularyHub';
import StudentExamAccessModal from './components/StudentExamAccessModal';
import ExamEngine from './components/ExamEngine';
import Footer from './components/Footer';
import TutorialModal from './components/TutorialModal';
import AboutModal from './components/AboutModal';
import PrivacyModal from './components/PrivacyModal';
import PwaInstallBanner from './components/PwaInstallBanner';
import AppUpdateModal from './components/AppUpdateModal';

const GEMINI_DIRECT_LINK = "https://gemini.google.com/";

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authRoleTarget, setAuthRoleTarget] = useState('student'); // 'student' o 'teacher'
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPwaInstallModal, setShowPwaInstallModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // Estados para el Módulo de Evaluación Sincronizada con PIN
  const [showExamAccessModal, setShowExamAccessModal] = useState(false);
  const [activeExamSession, setActiveExamSession] = useState(null); // { grade, unit, pinData }

  const [selectedGrade, setSelectedGrade] = useState(null); // '6', '7', '9'
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'dictionary', 'hub', 'faq', 'messages', 'chat', 'teacher'

  useEffect(() => {
    const savedUser = localStorage.getItem('motor_pedagogico_current_user');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Error cargando usuario guardado:", e);
      }
    }

    // Comprobar si el usuario ya vio el anuncio de la versión 2.3
    const hasSeenV23 = localStorage.getItem('english_eoh_update_seen_v2_3');
    if (!hasSeenV23) {
      setShowUpdateModal(true);
      localStorage.setItem('english_eoh_update_seen_v2_3', 'true');
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('motor_pedagogico_current_user');
    setCurrentUser(null);
    setSelectedGrade(null);
    setActiveTab('dashboard');
  };

  const handleOpenTeacherPanel = () => {
    setSelectedGrade(null);
    if (currentUser && currentUser.role === 'teacher') {
      setActiveTab('teacher');
    } else {
      setAuthRoleTarget('teacher');
      setShowAuthModal(true);
    }
  };

  const handleOpenStudentLogin = () => {
    setAuthRoleTarget('student');
    setShowAuthModal(true);
  };

  const handleNavClick = (tab) => {
    setSelectedGrade(null);
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectGrade = (grade) => {
    if (!currentUser) {
      setAuthRoleTarget('student');
      setShowAuthModal(true);
    } else {
      setActiveTab('dashboard');
      setSelectedGrade(grade);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans flex flex-col justify-between bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.25),rgba(255,255,255,0))]">
      <div>
        {/* BARRA DE NAVEGACIÓN Y BANNER INSTITUCIONAL */}
        <Navbar 
          activeTab={activeTab} 
          selectedGrade={selectedGrade}
          currentUser={currentUser}
          handleNavClick={handleNavClick}
          handleSelectGrade={handleSelectGrade}
          handleOpenTeacherPanel={handleOpenTeacherPanel}
          handleOpenStudentLogin={handleOpenStudentLogin}
          handleOpenExamAccessModal={() => setShowExamAccessModal(true)}
          handleLogout={handleLogout}
          setShowTutorialModal={setShowTutorialModal}
          onOpenInstall={() => setShowPwaInstallModal(true)}
          onOpenUpdateModal={() => setShowUpdateModal(true)}
        />

        {/* CONTENIDO PRINCIPAL DE LA PLATAFORMA */}
        <main className="p-4 md:p-10 max-w-6xl mx-auto space-y-8">
          {activeExamSession ? (
            <div className="bg-slate-800/60 border border-purple-500/50 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl space-y-6">
              <div className="flex justify-between items-center border-b border-slate-700 pb-4">
                <div>
                  <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Evaluación Sincronizada con PIN</span>
                  <h2 className="text-xl md:text-2xl font-extrabold text-white">Examen Grado {activeExamSession.grade}° - Unidad {activeExamSession.unit}</h2>
                </div>
                <button 
                  onClick={() => setActiveExamSession(null)}
                  className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-xl transition cursor-pointer"
                >
                  ✕ Salir de Evaluación
                </button>
              </div>
              <ExamEngine 
                grade={activeExamSession.grade}
                unit={activeExamSession.unit}
                currentUser={currentUser}
                pinData={activeExamSession.pinData}
                onFinishExam={() => {
                  setActiveExamSession(null);
                  setActiveTab('history');
                }}
              />
            </div>
          ) : activeTab === 'teacher' ? (
            <div className="space-y-6">
              <TeacherDashboard />
            </div>
          ) : selectedGrade ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl space-y-6">
              <div className="flex justify-between items-center border-b border-slate-700 pb-4">
                <div>
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Módulo Académico Activo</span>
                  <h2 className="text-xl md:text-2xl font-extrabold text-white">English EOH - Grado {selectedGrade}°</h2>
                </div>
                <button 
                  onClick={() => setSelectedGrade(null)}
                  className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-xl transition cursor-pointer"
                >
                  ← Volver a Grados
                </button>
              </div>
              <MissionPlayer grade={selectedGrade} currentUser={currentUser} />
            </div>
          ) : activeTab === 'history' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <StudentProgressHistory 
                currentUser={currentUser} 
                onNavigateToGrades={() => { setSelectedGrade(null); setActiveTab('dashboard'); }} 
              />
            </div>
          ) : activeTab === 'messages' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <StudentMessages currentUser={currentUser} />
            </div>
          ) : activeTab === 'chat' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <StudentTutorChat />
            </div>
          ) : activeTab === 'repository' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <WorksheetRepository 
                onBack={() => {
                  setSelectedGrade(null);
                  setActiveTab(currentUser?.role === 'teacher' ? 'teacher' : 'dashboard');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          ) : activeTab === 'unit_vocab' ? (
            <div className="bg-slate-800/60 border border-emerald-500/40 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <UnitVocabularyHub />
            </div>
          ) : activeTab === 'grammar' ? (
            <div className="bg-slate-800/60 border border-blue-500/40 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <GrammarHub />
            </div>
          ) : activeTab === 'hub' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <VocabularyHub />
            </div>
          ) : activeTab === 'dictionary' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <VocabularyDictionary />
            </div>
          ) : activeTab === 'faq' ? (
            <div className="bg-slate-800/60 border border-slate-700 p-6 md:p-8 rounded-3xl backdrop-blur-md shadow-2xl">
              <FAQSection />
            </div>
          ) : (
            <>
              {/* BLOQUE PRINCIPAL: BIENVENIDA E INSTITUCIÓN */}
              <section className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-slate-800 p-6 md:p-8 rounded-3xl shadow-2xl backdrop-blur-xl space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <span className="inline-block bg-blue-500/20 text-blue-300 text-[10px] md:text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-wider border border-blue-400/30 mb-2">
                      Institución Educativa Enrique Olaya Herrera — Medellín
                    </span>
                    <h2 className="text-2xl md:text-4xl font-black tracking-tight text-white flex items-center gap-3">
                      <span>🏫</span> English EOH — Plataforma Pedagógica
                    </h2>
                    <p className="text-slate-300 text-xs md:text-sm max-w-2xl mt-1">
                      Aprende inglés interactivo con la metodología Task-Based Learning. Selecciona una zona de trabajo para comenzar.
                    </p>
                  </div>

                  {/* BOTÓN DESTACADO: MI AVANCE Y NOTAS */}
                  <button 
                    onClick={() => {
                      if (!currentUser) handleOpenStudentLogin();
                      else { setSelectedGrade(null); setActiveTab('history'); }
                    }}
                    className="btn-3d btn-3d-emerald px-6 py-3.5 text-xs md:text-sm gap-2 shrink-0 shadow-2xl"
                  >
                    📊 Mi Avance y Notas 🚀
                  </button>
                </div>
              </section>

              {/* ZONAS DE TRABAJO ESTRUCTURADAS (ZONAS 1, 2 Y 3) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* ZONA 1: EVALUACIÓN Y ACCESO CON PIN DINÁMICO */}
                <div className="bg-slate-900/90 border border-purple-500/40 p-6 rounded-3xl shadow-xl flex flex-col justify-between space-y-4 hover:border-purple-400 transition">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-purple-400 bg-purple-950 px-2.5 py-1 rounded-full border border-purple-500/30">
                        Zona 1: Evaluaciones
                      </span>
                      <span className="text-[11px] font-bold text-purple-300">🔐 PIN 10 Min</span>
                    </div>

                    <h3 className="text-lg font-black text-white flex items-center gap-2">
                      <span>📝</span> Pruebas Saber ICFES
                    </h3>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      Ingresa con el PIN de 6 dígitos otorgado por el docente para presentar evaluaciones automatizadas de 15 preguntas.
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-800 space-y-2.5">
                    <button 
                      onClick={() => setShowExamAccessModal(true)}
                      className="btn-3d btn-3d-purple w-full py-3 text-xs gap-2"
                    >
                      📝 Acceso a Evaluación (Con PIN) 🚀
                    </button>

                    <button 
                      onClick={handleOpenTeacherPanel}
                      className="w-full text-center text-purple-300 hover:text-white text-xs font-bold py-1.5 transition cursor-pointer"
                    >
                      👨‍🏫 Panel Docente (Generar PIN) →
                    </button>
                  </div>
                </div>

                {/* ZONA 2: ESTUDIO, VOCABULARIO Y REPASO GRAMATICAL */}
                <div className="bg-slate-900/90 border border-blue-500/40 p-6 rounded-3xl shadow-xl flex flex-col justify-between space-y-4 hover:border-blue-400 transition">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-blue-400 bg-blue-950 px-2.5 py-1 rounded-full border border-blue-500/30">
                        Zona 2: Estudio & Repaso
                      </span>
                      <span className="text-[11px] font-bold text-blue-300">📚 15 Unidades</span>
                    </div>

                    <h3 className="text-lg font-black text-white flex items-center gap-2">
                      <span>💡</span> Repaso Didáctico
                    </h3>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      Repasa reglas gramaticales, fórmulas sintácticas, tips ICFES y vocabulario con oraciones bilingües de ejemplo.
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-800 space-y-2.5">
                    <button 
                      onClick={() => handleNavClick('unit_vocab')}
                      className="btn-3d btn-3d-emerald w-full py-2.5 text-xs gap-2"
                    >
                      📚 Vocabulario por Unidad (10+ Palabras)
                    </button>

                    <button 
                      onClick={() => handleNavClick('grammar')}
                      className="btn-3d btn-3d-blue w-full py-2.5 text-xs gap-2"
                    >
                      💡 Guías Gramaticales por Unidad
                    </button>
                  </div>
                </div>

                {/* ZONA 3: TUTOR VIRTUAL INTELIGENTE (PROFESOR DIDIER AI) */}
                <div className="bg-slate-900/90 border border-cyan-500/40 p-6 rounded-3xl shadow-xl flex flex-col justify-between space-y-4 hover:border-cyan-400 transition">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400 bg-cyan-950 px-2.5 py-1 rounded-full border border-cyan-500/30">
                        Zona 3: Tutoría Virtual
                      </span>
                      <span className="text-[11px] font-bold text-cyan-300">⚡ IA Integrada</span>
                    </div>

                    <h3 className="text-lg font-black text-white flex items-center gap-2">
                      <span>🤖</span> Profesor Didier AI
                    </h3>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      Resuelve dudas en tiempo real, solicita ejemplos adicionales y conversa interactivamente en inglés o español.
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-800 space-y-2.5">
                    <button 
                      onClick={() => handleNavClick('chat')}
                      className="btn-3d btn-3d-cyan w-full py-3 text-xs gap-2"
                    >
                      🤖 Hablar con Profesor Didier AI 🚀
                    </button>

                    <button 
                      onClick={() => setShowTutorialModal(true)}
                      className="w-full text-center text-sky-300 hover:text-white text-xs font-bold py-1.5 transition cursor-pointer"
                    >
                      ❓ ¿Cómo funciona la plataforma? →
                    </button>
                  </div>
                </div>

              </div>

              {/* SECCIÓN DE GRADOS ACADÉMICOS INTERACTIVOS */}
              <div className="pt-4 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Plan de Estudios Oficial</span>
                    <h3 className="text-xl md:text-2xl font-black text-white flex items-center gap-2">
                      <span>📖</span> Módulos Académicos por Grado
                    </h3>
                  </div>
                  <span className="text-xs font-bold text-slate-400 bg-slate-900 px-3 py-1 rounded-full border border-slate-800">
                    Grados 6°, 7° y 9°
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Tarjeta Grado 6° */}
                  <div 
                    onClick={() => handleSelectGrade('6')}
                    className="card-3d-grade bg-slate-900/90 border border-blue-500/30 p-6 flex flex-col justify-between"
                  >
                    <div>
                      <div className="text-blue-400 font-black text-xl mb-1 flex items-center justify-between">
                        <span>Grado 6°</span>
                        <span className="text-[10px] bg-blue-950 text-blue-300 px-2.5 py-0.5 rounded-full border border-blue-500/30 font-bold">Nivel A1</span>
                      </div>
                      <h3 className="font-bold text-white mb-3 text-sm md:text-base">Fundamentos y Deletreo</h3>
                      
                      <ul className="text-slate-300 text-xs space-y-2 mb-6">
                        <li className="flex items-start gap-1.5"><span className="text-blue-400 font-bold">• U1:</span> Saludos, alfabeto y datos personales</li>
                        <li className="flex items-start gap-1.5"><span className="text-blue-400 font-bold">• U2:</span> La familia, la casa y posesivos</li>
                        <li className="flex items-start gap-1.5"><span className="text-blue-400 font-bold">• U3:</span> Rutinas diarias y Present Simple</li>
                        <li className="flex items-start gap-1.5"><span className="text-blue-400 font-bold">• U4:</span> Materias del colegio y gustos</li>
                        <li className="flex items-start gap-1.5"><span className="text-blue-400 font-bold">• U5:</span> Habilidades (Can / Can't) y hobbies</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t border-slate-800 flex flex-col gap-2.5">
                      <span className="text-xs font-black text-blue-400">Ingresar a Misiones 6° →</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavClick('repository');
                        }}
                        className="btn-3d btn-3d-amber py-2 text-[11px] font-bold gap-1"
                      >
                        📁 Ver Guías y Talleres T2 y T3 ↗
                      </button>
                    </div>
                  </div>

                  {/* Tarjeta Grado 7° */}
                  <div 
                    onClick={() => handleSelectGrade('7')}
                    className="card-3d-grade bg-slate-900/90 border border-indigo-500/30 p-6 flex flex-col justify-between"
                  >
                    <div>
                      <div className="text-indigo-400 font-black text-xl mb-1 flex items-center justify-between">
                        <span>Grado 7°</span>
                        <span className="text-[10px] bg-indigo-950 text-indigo-300 px-2.5 py-0.5 rounded-full border border-indigo-500/30 font-bold">Nivel A1-A2</span>
                      </div>
                      <h3 className="font-bold text-white mb-3 text-sm md:text-base">Task-Based Learning</h3>

                      <ul className="text-slate-300 text-xs space-y-2 mb-6">
                        <li className="flex items-start gap-1.5"><span className="text-indigo-400 font-bold">• U1:</span> Sentimientos, emociones y salud</li>
                        <li className="flex items-start gap-1.5"><span className="text-indigo-400 font-bold">• U2:</span> Tiempo libre y frecuencia</li>
                        <li className="flex items-start gap-1.5"><span className="text-indigo-400 font-bold">• U3:</span> Lugares de la ciudad y direcciones</li>
                        <li className="flex items-start gap-1.5"><span className="text-indigo-400 font-bold">• U4:</span> Alimentación y sustantivos contables</li>
                        <li className="flex items-start gap-1.5"><span className="text-indigo-400 font-bold">• U5:</span> Pasado Simple (Was/Were) y biografías</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t border-slate-800 flex flex-col gap-2.5">
                      <span className="text-xs font-black text-indigo-400">Ingresar a Misiones 7° →</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavClick('repository');
                        }}
                        className="btn-3d btn-3d-amber py-2 text-[11px] font-bold gap-1"
                      >
                        📁 Ver Guías y Talleres T2 y T3 ↗
                      </button>
                    </div>
                  </div>

                  {/* Tarjeta Grado 9° */}
                  <div 
                    onClick={() => handleSelectGrade('9')}
                    className="card-3d-grade bg-slate-900/90 border border-emerald-500/30 p-6 flex flex-col justify-between"
                  >
                    <div>
                      <div className="text-emerald-400 font-black text-xl mb-1 flex items-center justify-between">
                        <span>Grado 9°</span>
                        <span className="text-[10px] bg-emerald-950 text-emerald-300 px-2.5 py-0.5 rounded-full border border-emerald-500/30 font-bold">Nivel A2-B1</span>
                      </div>
                      <h3 className="font-bold text-white mb-3 text-sm md:text-base">Competencias Avanzadas</h3>

                      <ul className="text-slate-300 text-xs space-y-2 mb-6">
                        <li className="flex items-start gap-1.5"><span className="text-emerald-400 font-bold">• U1:</span> Conciencia ambiental y Voz Pasiva</li>
                        <li className="flex items-start gap-1.5"><span className="text-emerald-400 font-bold">• U2:</span> Inventos, tecnología y "Used to"</li>
                        <li className="flex items-start gap-1.5"><span className="text-emerald-400 font-bold">• U3:</span> Viajes, culturas y Present Perfect</li>
                        <li className="flex items-start gap-1.5"><span className="text-emerald-400 font-bold">• U4:</span> Futuro (Will vs Going to) y Condicionales</li>
                        <li className="flex items-start gap-1.5"><span className="text-emerald-400 font-bold">• U5:</span> Verbos modales y lecturas tipo ICFES</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t border-slate-800 flex flex-col gap-2.5">
                      <span className="text-xs font-black text-emerald-400">Ingresar a Misiones 9° →</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavClick('repository');
                        }}
                        className="btn-3d btn-3d-amber py-2 text-[11px] font-bold gap-1"
                      >
                        📁 Ver Guías y Talleres T2 y T3 ↗
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* ACCESOS RÁPIDOS INFERIORES EN ESTILO 3D CÁPSULA */}
      <div className="print:hidden max-w-6xl mx-auto w-full px-6 mb-6 flex flex-wrap justify-center items-center gap-3 border-t border-slate-800/80 pt-6">
        <button 
          onClick={() => {
            if (!currentUser) {
              handleOpenStudentLogin();
            } else {
              handleNavClick('history');
            }
          }}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'history' ? 'btn-3d-emerald scale-105' : 'btn-3d-slate'
          }`}
        >
          📊 Mi Avance y Notas
        </button>
        <button 
          onClick={() => handleNavClick('unit_vocab')}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'unit_vocab' ? 'btn-3d-emerald scale-105' : 'btn-3d-slate'
          }`}
        >
          📚 Vocabulario por Unidad
        </button>
        <button 
          onClick={() => handleNavClick('grammar')}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'grammar' ? 'btn-3d-blue scale-105' : 'btn-3d-slate'
          }`}
        >
          💡 Guías Gramaticales
        </button>
        <button 
          onClick={() => handleNavClick('repository')}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'repository' ? 'btn-3d-amber scale-105' : 'btn-3d-slate'
          }`}
        >
          📁 Guías Drive (PDF)
        </button>

        <button 
          onClick={() => handleNavClick('messages')}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'messages' ? 'btn-3d-blue scale-105' : 'btn-3d-slate'
          }`}
        >
          💬 Mensajes al Profe
        </button>
        <button 
          onClick={() => setShowPwaInstallModal(true)}
          className="btn-3d btn-3d-emerald px-5 py-2.5 text-xs gap-2 shadow-lg"
        >
          📲 Instalar App en Celular
        </button>
        <button 
          onClick={() => handleNavClick('faq')}
          className={`btn-3d px-5 py-2.5 text-xs gap-2 ${
            activeTab === 'faq' ? 'btn-3d-blue scale-105' : 'btn-3d-slate'
          }`}
        >
          ❓ Preguntas Frecuentes (FAQ)
        </button>
        
        <a 
          href="mailto:didier.alvarez@medellin.edu.co" 
          className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700 px-5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 shadow-md"
        >
          📧 didier.alvarez@medellin.edu.co
        </a>
      </div>

      {/* PIE DE PÁGINA */}
      <Footer 
        onOpenAbout={() => setShowAboutModal(true)}
        onOpenPrivacy={() => setShowPrivacyModal(true)}
        onOpenInstall={() => setShowPwaInstallModal(true)}
      />

      {/* MODAL AUTENTICACIÓN */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-md w-full p-8 relative shadow-2xl">
            <button 
              onClick={() => setShowAuthModal(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-white font-bold text-xl bg-slate-800 w-8 h-8 rounded-full flex items-center justify-center transition cursor-pointer"
            >
              ✕
            </button>
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-white tracking-tight">
                {authRoleTarget === 'teacher' ? '👨‍🏫 Acceso Profesor / Administrador' : '🎓 Acceso Estudiantes EOH'}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                {authRoleTarget === 'teacher' ? 'Ingreso para monitoreo de la plataforma' : 'Ingresa con tu ID EOH y fecha de nacimiento'}
              </p>
            </div>
            <AuthScreen 
              authRoleTarget={authRoleTarget}
              onLoginSuccess={(user) => {
                setCurrentUser(user);
                setShowAuthModal(false);
                if (user.role === 'teacher') {
                  setActiveTab('teacher');
                }
              }} 
            />
          </div>
        </div>
      )}

      {/* MODALES REFACTORIZADOS */}
      <StudentExamAccessModal 
        isOpen={showExamAccessModal} 
        onClose={() => setShowExamAccessModal(false)} 
        onStartExam={(examData) => setActiveExamSession(examData)} 
        currentUser={currentUser} 
      />
      <TutorialModal isOpen={showTutorialModal} onClose={() => setShowTutorialModal(false)} />
      <AboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
      <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <PwaInstallBanner isOpen={showPwaInstallModal} onClose={() => setShowPwaInstallModal(false)} />
      <AppUpdateModal isOpen={showUpdateModal} onClose={() => setShowUpdateModal(false)} />

    </div>
  );
}

export default App;